import {
  markWord,
  readBopomofo,
  readWord,
  type Syllable,
  type ToneNotation,
  writeSyllable,
} from "@kensio/pinyinjs";

/**
 * Reading a text that is already romanised, and writing it another way.
 *
 * The pinyin and bopomofo pages need no dictionary at all, which is worth
 * saying plainly because it is the reason they are separate pages: converting
 * `nǐ hǎo` to `ㄋㄧˇ ㄏㄠˇ` is a spelling operation on syllables that are
 * already spelled out, so there is nothing to look up and nothing to download.
 * The hard problem the hanzi page has — which reading does this character take
 * here — does not arise.
 *
 * What is left is tokenising: finding the words in a text and passing
 * everything between them through untouched, so that punctuation, English and
 * anything that is not a syllable survive the round trip exactly as typed.
 */

/**
 * One piece of a parsed text: a word that was read, or anything else.
 *
 * `syllables` is undefined for the text between words, and also for a word
 * that could not be read as syllables at all. Both are passed through as
 * written, which is what makes the tools forgiving of a typo: one unreadable
 * word does not cost you the rest of the sentence.
 */
export interface ParsedPiece {
  readonly text: string;
  readonly syllables: readonly Syllable[] | undefined;
}

/** How a word is read into syllables. */
export type ReadWord = (word: string) => readonly Syllable[] | undefined;

/**
 * An input script: how to find its words, and how to read one.
 *
 * The pattern belongs with the reader because the two scripts disagree about
 * what a word looks like, and getting that wrong is silent. Bopomofo's neutral
 * tone is `˙` written *in front of* the syllable, and `˙` is a modifier symbol
 * rather than a letter — so a pattern built out of `\p{L}` drops it, and
 * `˙ㄇㄚ` reads as a first-tone `mā` with a stray dot beside it. The other
 * three marks, ˊ ˇ and ˋ, are modifier *letters* and would have been caught.
 */
export interface InputScript {
  /** Must be global: {@link parseText} iterates every match. */
  readonly words: RegExp;
  readonly read: ReadWord;
}

/**
 * Pinyin: letters and combining marks, because a tone mark may arrive either
 * precomposed or as a combining accent; digits, because `ni3` is how tone
 * numbers are written; and the apostrophes and hyphen, which mark syllable
 * boundaries inside a word rather than breaks between words.
 */
export const pinyinScript: InputScript = {
  words: /[\p{L}\p{M}\d'’‘-]+/gu,
  read: (word) => readWord(word),
};

/**
 * Bopomofo: one syllable at a time, with the tone mark on whichever end it is
 * written.
 *
 * Bopomofo spaces its syllables, so a token here is normally one syllable —
 * and `readBopomofo` reads exactly one, which is why the pattern matches one
 * rather than a run of them.
 */
export const bopomofoScript: InputScript = {
  words: /˙?[ㄅ-ㄯㆠ-ㆿ]+[ˊˇˋ˙]?/gu,
  read: (word) => {
    const syllable = readBopomofo(word);

    return syllable === undefined ? undefined : [syllable];
  },
};

/**
 * Split a text into words and the text between them.
 *
 * The pieces concatenate back to exactly the input, so a caller that rewrites
 * only the words it recognised loses nothing.
 */
export function parseText(
  text: string,
  script: InputScript,
): readonly ParsedPiece[] {
  const pieces: ParsedPiece[] = [];
  let at = 0;

  for (const match of text.matchAll(script.words)) {
    const start = match.index;

    if (start > at) {
      pieces.push({ text: text.slice(at, start), syllables: undefined });
    }

    pieces.push({ text: match[0], syllables: script.read(match[0]) });
    at = start + match[0].length;
  }

  if (at < text.length) {
    pieces.push({ text: text.slice(at), syllables: undefined });
  }

  return pieces;
}

/**
 * Whether a word was written with a capital.
 *
 * Reading a syllable throws its capitals away — case is not part of a
 * syllable — so `Xī'ān` would come back as `xī'ān` unless the capital is
 * carried across separately from the word it was written on.
 */
export function isCapitalised(word: string): boolean {
  const first = /\p{L}/u.exec(word)?.[0];

  return first !== undefined && first !== first.toLowerCase();
}

/** Put a capital on the first letter of a word. */
export function capitalised(word: string): string {
  return word.replace(/\p{L}/u, (letter) => letter.toUpperCase());
}

/**
 * Write a parsed text with one word writer, leaving everything else alone.
 *
 * `hasCapitals` is whether the output system writes them at all. The three
 * romanisations do, because they spell Chinese in the Latin alphabet and
 * inherit what that alphabet does with a proper noun. Bopomofo is a script
 * with no case, and IPA's letters are symbols rather than an alphabet — `[T]`
 * is not a larger `[t]` but a symbol the IPA has no use for — so neither takes
 * one. This mirrors the package's own table for its own reasons.
 */
export function writeParsed(
  pieces: readonly ParsedPiece[],
  write: (syllables: readonly Syllable[]) => string,
  hasCapitals = true,
): string {
  return pieces
    .map((piece) => {
      if (piece.syllables === undefined) {
        return piece.text;
      }

      const written = write(piece.syllables);

      return hasCapitals && isCapitalised(piece.text)
        ? capitalised(written)
        : written;
    })
    .join("");
}

/**
 * Write a word as pinyin syllable by syllable, each carrying the 隔音符号 it
 * takes.
 *
 * Per syllable rather than as one string because the pinyin panel colours each
 * syllable by its tone, and the apostrophe belongs to the boundary rather than
 * to either side of it — so it cannot be added by a caller writing one
 * syllable at a time. `markWord` is the package's answer to exactly this.
 */
export function markPinyinWord(
  syllables: readonly Syllable[],
  notation: ToneNotation,
): readonly string[] {
  return markWord(
    syllables.map((syllable) => writeSyllable(syllable, notation)),
  );
}

/**
 * Write a word as pinyin, with the 隔音符号 where the orthography wants one.
 *
 * Not one of the package's `WriteWord` functions, because pinyin's own join is
 * the apostrophe rule rather than a fixed separator — and it is a rule about
 * spelling, so it applies to what was written rather than to what was read.
 */
export function writePinyinWord(
  syllables: readonly Syllable[],
  notation: ToneNotation,
): string {
  return markPinyinWord(syllables, notation).join("");
}
